import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  ShieldCheck, ShieldAlert, ShieldOff, Shield, Loader2, ChevronDown,
  ChevronRight, FileSearch, EyeOff, History,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TONE = {
  ok: { ring: "border-emerald-400/30 bg-emerald-400/[0.04]",
        fg: "text-emerald-300", Icon: ShieldCheck },
  warn: { ring: "border-amber-400/30 bg-amber-400/[0.04]",
          fg: "text-amber-300", Icon: ShieldAlert },
  skipped: { ring: "border-zinc-500/30 bg-zinc-500/[0.04]",
             fg: "text-zinc-300", Icon: ShieldOff },
  neutral: { ring: "border-white/[0.08] bg-white/[0.02]",
             fg: "text-zinc-400", Icon: Shield },
};

function Collapsible({ title, count, testId, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="rounded-xl border border-white/[0.06] bg-white/[0.01] mt-3" data-testid={testId}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid={`${testId}-toggle`}
        className="w-full flex items-center justify-between px-4 py-2.5 text-left"
      >
        <div className="flex items-center gap-2 text-[12.5px] text-zinc-300">
          {open ? <ChevronDown className="h-3.5 w-3.5 text-zinc-500" /> : <ChevronRight className="h-3.5 w-3.5 text-zinc-500" />}
          <span>{title}</span>
          <span className="text-[11px] text-zinc-500">({count})</span>
        </div>
      </button>
      {open && <div className="px-4 pb-3 border-t border-white/[0.04]">{children}</div>}
    </div>
  );
}

/**
 * Detailed screening summary panel — drop-in for the cross-check page.
 *
 * Surfaces:
 *  · Headline + tone (clean / findings / skipped / never)
 *  · Last run metadata (provider, ts, targets, counts)
 *  · Targets-checked list with role pill
 *  · Skip remediation copy (when applicable)
 *  · Last 5 runs (includes skips)
 *  · Dismissed click-through (sticky-dismissal list)
 *  · Redacted click-through (output-filter scrub list)
 */
export default function ScreeningSummaryPanel({ merchantId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(
        `${API}/admin/merchants/${merchantId}/screening-summary`,
        { credentials: "include" },
      );
      if (!r.ok) throw new Error(`${r.status}`);
      const body = await r.json();
      setData(body);
    } catch (_) {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [merchantId]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="flex items-center text-zinc-500 py-6" data-testid="screening-summary-loading">
        <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading screening summary…
      </div>
    );
  }
  if (!data) return null;

  const tone = TONE[data.headline?.tone] || TONE.neutral;
  const Icon = tone.Icon;
  const lr = data.last_run;
  const targets = data.last_run_targets || [];
  const history = data.history || [];
  const dismissed = data.dismissed || [];
  const redacted = data.redacted_findings || [];

  return (
    <div data-testid="screening-summary-panel" className="space-y-4">
      {/* Headline */}
      <div className={`rounded-2xl border ${tone.ring} p-5`} data-testid={`screening-summary-headline-${data.headline.kind}`}>
        <div className="flex items-start gap-3">
          <Icon className={`h-5 w-5 ${tone.fg} mt-0.5`} />
          <div className="flex-1 min-w-0">
            <div className={`text-[14.5px] font-medium ${tone.fg}`}>{data.headline.text}</div>
            {data.last_run_remediation && (
              <div className="mt-2 text-[12px] text-zinc-400 leading-relaxed" data-testid="screening-summary-remediation">
                {data.last_run_remediation}
              </div>
            )}
            {lr && (
              <dl className="mt-3 grid grid-cols-2 lg:grid-cols-4 gap-3 text-[11.5px]">
                <div>
                  <dt className="text-[10px] uppercase tracking-[0.16em] text-zinc-500">Run at</dt>
                  <dd className="text-zinc-300 font-mono">{(lr.completed_at || lr.triggered_at || "").slice(0, 19).replace("T", " ")}</dd>
                </div>
                <div>
                  <dt className="text-[10px] uppercase tracking-[0.16em] text-zinc-500">Provider</dt>
                  <dd className="text-zinc-300">{lr.provider || "—"}</dd>
                </div>
                <div>
                  <dt className="text-[10px] uppercase tracking-[0.16em] text-zinc-500">Targets</dt>
                  <dd className="text-zinc-300">{lr.targets_screened ?? 0}</dd>
                </div>
                <div>
                  <dt className="text-[10px] uppercase tracking-[0.16em] text-zinc-500">Open findings</dt>
                  <dd className="text-zinc-300">{data.open_findings_count}</dd>
                </div>
              </dl>
            )}
          </div>
        </div>
      </div>

      {/* Targets checked */}
      {targets.length > 0 && (
        <div className="rounded-xl border border-white/[0.06] bg-white/[0.01] p-4" data-testid="screening-summary-targets">
          <div className="text-[10.5px] uppercase tracking-[0.18em] text-zinc-500 mb-3">— Targets checked</div>
          <div className="flex flex-wrap gap-2">
            {targets.map((t, i) => (
              <div
                key={`${t.name}-${i}`}
                data-testid={`screening-summary-target-${i}`}
                className="rounded-lg border border-white/[0.08] bg-white/[0.02] px-3 py-1.5 inline-flex items-center gap-2"
              >
                <span className="text-[10px] uppercase tracking-[0.16em] text-zinc-500">{t.target_type}</span>
                <span className="text-[12.5px] text-white">{t.name}</span>
              </div>
            ))}
          </div>
          {lr?.categories && Array.isArray(lr.categories) && (
            <div className="mt-3 text-[11px] text-zinc-500">
              Categories: <span className="text-zinc-300">{lr.categories.join(", ")}</span>
            </div>
          )}
        </div>
      )}

      {/* Suppressed (dismissed sticky list) */}
      <Collapsible
        title="Suppressed (previously dismissed)"
        count={dismissed.length}
        testId="screening-summary-suppressed"
      >
        {dismissed.length === 0 ? (
          <div className="py-3 text-[12px] text-zinc-500" data-testid="screening-summary-suppressed-empty">
            None — no findings have been dismissed for this merchant yet.
          </div>
        ) : (
          <table className="w-full mt-2">
            <thead>
              <tr className="text-[10px] uppercase tracking-[0.16em] text-zinc-500">
                <th className="text-left py-2 font-normal">Target</th>
                <th className="text-left py-2 font-normal">Category</th>
                <th className="text-left py-2 font-normal">Source</th>
                <th className="text-left py-2 font-normal">Dismissed by</th>
              </tr>
            </thead>
            <tbody>
              {dismissed.map((d) => (
                <tr key={d.screening_finding_id} className="border-t border-white/[0.04]">
                  <td className="py-2 text-[12px] text-zinc-300">
                    <span className="text-[10px] uppercase tracking-[0.16em] text-zinc-500 mr-2">{d.target_type}</span>
                    {d.target_name}
                  </td>
                  <td className="py-2 text-[12px] text-zinc-400">{d.category}</td>
                  <td className="py-2 text-[12px]">
                    {d.source_url ? (
                      <a href={d.source_url} target="_blank" rel="noreferrer" className="text-[#00E5FF] hover:underline truncate inline-block max-w-[280px]">
                        {d.source_title || d.source_url}
                      </a>
                    ) : (
                      <span className="text-zinc-500">—</span>
                    )}
                  </td>
                  <td className="py-2 text-[11.5px] text-zinc-500">
                    {d.reviewed_by || "—"}
                    <span className="block text-[10px] text-zinc-600">{(d.reviewed_at || "").slice(0, 10)}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Collapsible>

      {/* Redacted (output-filter scrub) */}
      <Collapsible
        title="Redacted (output-filter scrubbed)"
        count={redacted.length}
        testId="screening-summary-redacted"
      >
        {redacted.length === 0 ? (
          <div className="py-3 text-[12px] text-zinc-500" data-testid="screening-summary-redacted-empty">
            None — no findings were scrubbed by the output filter.
          </div>
        ) : (
          <table className="w-full mt-2">
            <thead>
              <tr className="text-[10px] uppercase tracking-[0.16em] text-zinc-500">
                <th className="text-left py-2 font-normal">Target</th>
                <th className="text-left py-2 font-normal">Category</th>
                <th className="text-left py-2 font-normal">Reason</th>
                <th className="text-left py-2 font-normal">Status</th>
              </tr>
            </thead>
            <tbody>
              {redacted.map((r) => (
                <tr key={r.screening_finding_id} className="border-t border-white/[0.04]">
                  <td className="py-2 text-[12px] text-zinc-300">
                    <span className="text-[10px] uppercase tracking-[0.16em] text-zinc-500 mr-2">{r.target_type}</span>
                    {r.target_name}
                  </td>
                  <td className="py-2 text-[12px] text-zinc-400">{r.category}</td>
                  <td className="py-2 text-[12px] text-zinc-400">
                    {(r.redaction_reasons || []).join(", ") || "—"}
                  </td>
                  <td className="py-2 text-[11.5px] text-zinc-400">{r.reviewed_status || "pending"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Collapsible>

      {/* History — last 5 runs */}
      <Collapsible
        title="Run history"
        count={history.length}
        testId="screening-summary-history"
        defaultOpen={history.length > 1}
      >
        {history.length === 0 ? (
          <div className="py-3 text-[12px] text-zinc-500">No prior runs.</div>
        ) : (
          <table className="w-full mt-2">
            <thead>
              <tr className="text-[10px] uppercase tracking-[0.16em] text-zinc-500">
                <th className="text-left py-2 font-normal">When</th>
                <th className="text-left py-2 font-normal">Status</th>
                <th className="text-left py-2 font-normal">Targets</th>
                <th className="text-left py-2 font-normal">Findings</th>
                <th className="text-left py-2 font-normal">Suppressed</th>
              </tr>
            </thead>
            <tbody>
              {history.map((h) => {
                const isOk = (h.status || "ok") === "ok";
                return (
                  <tr key={h.run_id} className="border-t border-white/[0.04]" data-testid={`screening-summary-history-row-${h.run_id}`}>
                    <td className="py-2 text-[11.5px] text-zinc-400 font-mono">{(h.triggered_at || "").slice(0, 19).replace("T", " ")}</td>
                    <td className="py-2 text-[12px]">
                      {isOk ? (
                        <span className="text-emerald-300">completed</span>
                      ) : (
                        <span className="text-zinc-400">skipped · {h.status}</span>
                      )}
                    </td>
                    <td className="py-2 text-[12px] text-zinc-400">{h.targets_screened ?? 0}</td>
                    <td className="py-2 text-[12px] text-zinc-400">
                      {(h.new_findings ?? 0) + (h.repeat_findings ?? 0)}
                    </td>
                    <td className="py-2 text-[12px] text-zinc-400">{h.suppressed_by_dismissal ?? 0}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </Collapsible>
    </div>
  );
}
